import { useReducer } from "react";
import { useDropzone, } from "react-dropzone";
import { useNProgress } from "@tanem/react-nprogress";
import delay from "delay";
import isPromise from "p-is-promise";
import { FileUploadError } from "./file-upload-error";
const initialState = {
    state: "inactive",
    fileName: undefined,
    errorMessage: undefined,
    isMouseOver: false,
};
const reducer = (state, action) => {
    switch (action.type) {
        case "DRAG_ENTER":
            return {
                ...state,
                state: "active",
                errorMessage: undefined,
            };
        case "DRAG_LEAVE":
            return {
                ...state,
                state: state.fileName ? "success" : "inactive",
            };
        case "MOUSE_ENTER":
            return {
                ...state,
                isMouseOver: true,
            };
        case "MOUSE_LEAVE":
            return {
                ...state,
                isMouseOver: false,
            };
        case "START_UPLOADING":
            return {
                state: "uploading",
                fileName: undefined,
                errorMessage: undefined,
                isMouseOver: false,
            };
        case "UPLOAD":
            return {
                state: "success",
                fileName: action.fileName,
                errorMessage: undefined,
                isMouseOver: false,
            };
        case "ERROR":
            return {
                state: "error",
                fileName: undefined,
                errorMessage: action.message,
                isMouseOver: false,
            };
        default:
            return state;
    }
};
export const useFileInput = ({ isDisabled, accept, maxSize, onUpload, }) => {
    const [state, dispatch] = useReducer(reducer, initialState);
    const dropzone = useDropzone({
        disabled: isDisabled,
        maxSize,
        maxFiles: 1,
        validator(file) {
            if (!file.name || !accept) {
                return null;
            }
            const extension = file.name.split(".").pop();
            if (!extension) {
                return {
                    code: "missing-extension",
                    message: "File must have an extension",
                };
            }
            if (!accept.includes(extension)) {
                return {
                    code: "not-allowed",
                    message: `Only ${accept.join(", ").toLowerCase()} files are allowed`,
                };
            }
            return null;
        },
        onDragEnter() {
            dispatch({
                type: "DRAG_ENTER",
            });
        },
        onDragLeave() {
            dispatch({
                type: "DRAG_LEAVE",
            });
        },
        async onDrop(files) {
            const file = files[0];
            if (!file) {
                return;
            }
            try {
                const promise = onUpload(file);
                if (isPromise(promise)) {
                    const abortController = new AbortController();
                    // Show upload progress only if `onUpload` promise takes longer than 200ms to resolve
                    await Promise.race([
                        promise.catch((error) => {
                            abortController.abort();
                            throw error;
                        }),
                        delay(200, { signal: abortController.signal }).then(() => {
                            dispatch({
                                type: "START_UPLOADING",
                            });
                        }),
                    ]);
                    // `Promise.race` might resolve with `delay` promise first,
                    // so we still need to wait for `onUpload` promise to resolve
                    await promise;
                    abortController.abort();
                    dispatch({
                        type: "UPLOAD",
                        fileName: file.name,
                    });
                    return;
                }
                dispatch({
                    type: "UPLOAD",
                    fileName: file.name,
                });
            }
            catch (error) {
                if (error instanceof FileUploadError) {
                    dispatch({
                        type: "ERROR",
                        message: error.message,
                    });
                    return;
                }
                dispatch({
                    type: "ERROR",
                    message: "Something went wrong, try again",
                });
                throw error;
            }
        },
        onError(error) {
            dispatch({
                type: "ERROR",
                message: error.message,
            });
        },
        onDropRejected(rejections) {
            dispatch({
                type: "ERROR",
                message: rejections[0]?.errors[0]?.message ?? "Unexpected error occurred",
            });
        },
    });
    const nProgress = useNProgress({
        isAnimating: state.state === "uploading",
    });
    return {
        ...state,
        uploadProgress: nProgress.progress * 100,
        getRootProps() {
            return {
                ...dropzone.getRootProps(),
                onMouseEnter() {
                    if (isDisabled) {
                        return;
                    }
                    dispatch({
                        type: "MOUSE_ENTER",
                    });
                },
                onMouseLeave() {
                    if (isDisabled) {
                        return;
                    }
                    dispatch({
                        type: "MOUSE_LEAVE",
                    });
                },
            };
        },
        getInputProps() {
            return {
                ...dropzone.getInputProps(),
                accept: accept
                    ? accept.map((extension) => `.${extension}`).join(",")
                    : undefined,
            };
        },
    };
};
